import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { API_BASE_URL } from "../config/api";
import "../styles/form.css";

export default function CalculatorForm({ setData, clientToApply, onClientApplied, onQuoteReady }) {
  const initialVehicle = {
    weight: "",
    width: "",
    height: "",
    length: "",
    machineType: "",
    machineCategory: "",
    trailerType: "",
    axleConfiguration: ""
  };

  const initialClient = {
    name: "",
    company: "",
    email: "",
    phone: ""
  };

  const initialTouched = {
    from: false,
    to: false,
    weight: false,
    width: false,
    height: false,
    machineType: false,
    clientName: false,
    clientEmail: false
  };

  const [vehicle, setVehicle] = useState(initialVehicle);
  const [client, setClient] = useState(initialClient);
  const [locations, setLocations] = useState({
    from: null,
    to: null
  });
  const [locationInputs, setLocationInputs] = useState({ from: "", to: "" });
  const [touched, setTouched] = useState(initialTouched);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mapsUnavailable, setMapsUnavailable] = useState(false);
  const [suggestions, setSuggestions] = useState({
    from: [],
    to: []
  });
  const [savedClients, setSavedClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [savingClient, setSavingClient] = useState(false);
  const [clientNotice, setClientNotice] = useState("");

  const fromRef = useRef(null);
  const toRef = useRef(null);
  const googleInitRef = useRef(false);

  const mapsApiKey =
    process.env.REACT_APP_GOOGLE_PLACES_API_KEY?.trim() ||
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY?.trim();

  const applyClientRecord = useCallback((record) => {
    if (!record) return;
    setSelectedClientId(record._id || "");
    setClient({
      name: record.name || "",
      company: record.company || "",
      email: record.email || "",
      phone: record.phone || ""
    });

    const prefs = record.defaultPreferences || {};
    setVehicle((prev) => ({
      ...prev,
      machineType: prefs.machineType || prev.machineType,
      machineCategory: prefs.machineCategory || prev.machineCategory,
      trailerType: prefs.trailerType || prev.trailerType,
      axleConfiguration: prefs.axleConfiguration || prev.axleConfiguration
    }));

    if (prefs.commonOrigin) {
      setLocationInputs((prev) => ({ ...prev, from: prefs.commonOrigin }));
      setLocations((prev) => ({
        ...prev,
        from: { address: prefs.commonOrigin, lat: null, lng: null }
      }));
    }
    if (prefs.commonDestination) {
      setLocationInputs((prev) => ({ ...prev, to: prefs.commonDestination }));
      setLocations((prev) => ({
        ...prev,
        to: { address: prefs.commonDestination, lat: null, lng: null }
      }));
    }
    setClientNotice(`Loaded client: ${record.name}`);
  }, []);

  const fetchSavedClients = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/clients`);
      const data = await res.json();
      if (res.ok && Array.isArray(data)) setSavedClients(data);
    } catch {
      setSavedClients([]);
    }
  }, []);

  useEffect(() => {
    fetchSavedClients();
  }, [fetchSavedClients]);

  useEffect(() => {
    if (clientToApply) {
      applyClientRecord(clientToApply);
      onClientApplied?.();
    }
  }, [clientToApply, applyClientRecord, onClientApplied]);

  const applySelectedLocation = useCallback((field, details) => {
    if (!details?.address) return;
    const selected = {
      address: details.address,
      lat: details.lat,
      lng: details.lng
    };
    setLocations((prev) => ({ ...prev, [field]: selected }));
    setLocationInputs((prev) => ({ ...prev, [field]: selected.address }));
    setTouched((prev) => ({ ...prev, [field]: true }));
  }, []);

  useEffect(() => {
    let cancelled = false;
    let cleanup = () => {};

    const scriptId = "google-maps-js-api";
    const ensureGoogleMapsLoaded = async () => {
      if (window.google?.maps?.places && window.google?.maps) return true;
      if (!mapsApiKey) {
        setError("");
        setMapsUnavailable(true);
        return false;
      }

      let script = document.getElementById(scriptId);
      const expectedKey = `key=${encodeURIComponent(mapsApiKey)}`;
      if (script?.src && !script.src.includes(expectedKey)) {
        script.remove();
        script = null;
      }

      if (!script) {
        script = document.createElement("script");
        script.id = scriptId;
        script.async = true;
        script.defer = true;
        script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
          mapsApiKey
        )}&loading=async&v=weekly`;
        document.head.appendChild(script);
      }

      await new Promise((resolve) => {
        if (window.google?.maps) return resolve();
        script.addEventListener("load", resolve, { once: true });
        script.addEventListener("error", resolve, { once: true });
      });

      if (!window.google?.maps) {
        setMapsUnavailable(true);
        setError("");
        return false;
      }

      if (!window.google?.maps?.places && typeof window.google?.maps?.importLibrary === "function") {
        try {
          await window.google.maps.importLibrary("places");
        } catch {
          setMapsUnavailable(true);
          setError("");
          return false;
        }
      }

      if (!window.google?.maps?.places) {
        setMapsUnavailable(true);
        setError("");
        return false;
      }

      setMapsUnavailable(false);
      setError("");
      return true;
    };

    const initLegacyAutocomplete = () => {
      const options = { types: ["geocode"] };
      const fromAuto = new window.google.maps.places.Autocomplete(fromRef.current, options);
      const toAuto = new window.google.maps.places.Autocomplete(toRef.current, options);

      const fromListener = fromAuto.addListener("place_changed", () => {
        const place = fromAuto.getPlace();
        applySelectedLocation("from", {
          address: place?.formatted_address,
          lat: place?.geometry?.location?.lat?.(),
          lng: place?.geometry?.location?.lng?.()
        });
      });
      const toListener = toAuto.addListener("place_changed", () => {
        const place = toAuto.getPlace();
        applySelectedLocation("to", {
          address: place?.formatted_address,
          lat: place?.geometry?.location?.lat?.(),
          lng: place?.geometry?.location?.lng?.()
        });
      });

      return () => {
        window.google.maps.event.removeListener(fromListener);
        window.google.maps.event.removeListener(toListener);
      };
    };

    const boot = async () => {
      if (googleInitRef.current) return;
      if (!fromRef.current || !toRef.current) return;
      const loaded = await ensureGoogleMapsLoaded();
      if (!loaded || cancelled) return;
      googleInitRef.current = true;
      // React owns these input nodes; avoid replacing them with custom elements,
      // which can trigger insertBefore/removeChild reconciliation crashes.
      cleanup = initLegacyAutocomplete();
    };

    boot();
    return () => {
      cancelled = true;
      cleanup();
    };
  }, [applySelectedLocation, mapsApiKey]);

  /* ================= VALIDATION ================= */

  const toNumber = (value) => Number(value);
  const fieldErrors = useMemo(() => {
    const errors = {};
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(client.email.trim());

    if (mapsUnavailable ? !locationInputs.from.trim() : !locations.from?.address) {
      errors.from = "Please select a valid origin.";
    }
    if (mapsUnavailable ? !locationInputs.to.trim() : !locations.to?.address) {
      errors.to = "Please select a valid destination.";
    }
    if (!(toNumber(vehicle.weight) > 0)) errors.weight = "Weight must be greater than 0.";
    if (!(toNumber(vehicle.width) > 0)) errors.width = "Width must be greater than 0.";
    if (!(toNumber(vehicle.height) > 0)) errors.height = "Height must be greater than 0.";
    if (!vehicle.machineType) errors.machineType = "Select machine type.";
    if (!client.name.trim()) errors.clientName = "Client name is required.";
    if (!emailOk) errors.clientEmail = "Enter a valid email.";
    return errors;
  }, [client, locationInputs, locations, mapsUnavailable, vehicle]);

  const isValid = Object.keys(fieldErrors).length === 0;
  const showFieldError = (name) => touched[name] && fieldErrors[name];

  /* ================= HANDLERS ================= */

  const handleVehicleChange = (field, value) => {
    setVehicle((prev) => ({ ...prev, [field]: value }));
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const handleClientChange = (field, value) => {
    setClient((prev) => ({ ...prev, [field]: value }));
    const map = { name: "clientName", email: "clientEmail" };
    if (map[field]) setTouched((prev) => ({ ...prev, [map[field]]: true }));
  };

  const handleClientPickerChange = (event) => {
    const id = event.target.value;
    setSelectedClientId(id);
    if (!id) return;
    const record = savedClients.find((item) => item._id === id);
    if (record) applyClientRecord(record);
  };

  const handleSaveClient = async () => {
    if (!client.name.trim()) {
      setClientNotice("Enter client name before saving.");
      return;
    }
    try {
      setSavingClient(true);
      setClientNotice("");
      const payload = {
        name: client.name.trim(),
        company: client.company.trim(),
        email: client.email.trim(),
        phone: client.phone.trim(),
        notes: "",
        defaultPreferences: {
          machineType: vehicle.machineType,
          machineCategory: vehicle.machineCategory,
          trailerType: vehicle.trailerType,
          axleConfiguration: vehicle.axleConfiguration,
          commonOrigin: locationInputs.from.trim(),
          commonDestination: locationInputs.to.trim()
        }
      };
      const method = selectedClientId ? "PUT" : "POST";
      const url = selectedClientId
        ? `${API_BASE_URL}/api/clients/${selectedClientId}`
        : `${API_BASE_URL}/api/clients`;
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Failed to save client");
      setSelectedClientId(data._id);
      setClientNotice(selectedClientId ? "Client updated." : "Client saved.");
      fetchSavedClients();
    } catch (err) {
      setClientNotice(err?.message || "Failed to save client.");
    } finally {
      setSavingClient(false);
    }
  };

  const handleLocationChange = (field, value) => {
    setLocationInputs((prev) => ({ ...prev, [field]: value }));
    setLocations((prev) => ({ ...prev, [field]: null }));
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const fetchSuggestions = async (field, input) => {
      if (!input || input.trim().length < 3) {
        setSuggestions((prev) => ({ ...prev, [field]: [] }));
        return;
      }
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/routes/autocomplete?input=${encodeURIComponent(input.trim())}`,
          { signal: controller.signal }
        );
        const data = await res.json();
        if (!cancelled && res.ok) {
          setSuggestions((prev) => ({
            ...prev,
            [field]: Array.isArray(data?.suggestions) ? data.suggestions : []
          }));
        }
      } catch {
        if (!cancelled) {
          setSuggestions((prev) => ({ ...prev, [field]: [] }));
        }
      }
    };

    const timer = setTimeout(() => {
      if (mapsUnavailable) {
        fetchSuggestions("from", locationInputs.from);
        fetchSuggestions("to", locationInputs.to);
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [locationInputs.from, locationInputs.to, mapsUnavailable]);

  const markAllTouched = () => {
    setTouched({
      from: true,
      to: true,
      weight: true,
      width: true,
      height: true,
      machineType: true,
      clientName: true,
      clientEmail: true
    });
  };

  /* ================= SUBMIT ================= */

  const handleSubmit = async (event) => {
    event.preventDefault();
    markAllTouched();

    if (!isValid) {
      setError("Please complete all required fields correctly.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const payload = {
        from: mapsUnavailable ? locationInputs.from.trim() : locations.from.address,
        to: mapsUnavailable ? locationInputs.to.trim() : locations.to.address,
        weight: Number(vehicle.weight),
        width: Number(vehicle.width),
        height: Number(vehicle.height),
        length: Number(vehicle.length) || undefined,
        machineDetails: {
          machineType: vehicle.machineType,
          machineCategory: vehicle.machineCategory,
          trailerType: vehicle.trailerType,
          axleConfiguration: vehicle.axleConfiguration
        },
        client: {
          name: client.name.trim(),
          company: client.company.trim(),
          email: client.email.trim(),
          phone: client.phone.trim()
        }
      };

      const res = await fetch(`${API_BASE_URL}/api/distance`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      });

      let data = {};
      try {
        data = await res.json();
      } catch {
        data = {};
      }

      if (!res.ok) throw new Error(data.error || "Failed");

      setData({
        ...data,
        form: {
          from: payload.from,
          to: payload.to,
          weight: Number(vehicle.weight),
          width: Number(vehicle.width),
          height: Number(vehicle.height),
          machineDetails: payload.machineDetails,
          client: payload.client
        }
      });
      onQuoteReady?.();
      setTouched(initialTouched);

    } catch (err) {
      setError(err?.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  /* ================= UI ================= */

  return (
    <form className="form" onSubmit={handleSubmit} noValidate>

      <div className="form-intro">
        <h3>New Quote</h3>
        <p className="form-intro__hint">Client, route, load — then generate pricing</p>
      </div>

      <p className="form-section-label">Saved client</p>
      <div className="client-picker-row">
        <select value={selectedClientId} onChange={handleClientPickerChange} aria-label="Saved client">
          <option value="">Select saved client...</option>
          {savedClients.map((item) => (
            <option key={item._id} value={item._id}>
              {item.name}{item.company ? ` (${item.company})` : ""}
            </option>
          ))}
        </select>
        <button type="button" className="btn-secondary" onClick={handleSaveClient} disabled={savingClient}>
          {savingClient ? "Saving..." : selectedClientId ? "Update Client" : "Save Client"}
        </button>
      </div>
      {clientNotice && <p className="client-notice">{clientNotice}</p>}

      <p className="form-section-label">Client details</p>
      <input
        placeholder="Client Name"
        value={client.name}
        onChange={(e) => handleClientChange("name", e.target.value)}
        onBlur={() => setTouched((prev) => ({ ...prev, clientName: true }))}
        className={showFieldError("clientName") ? "input-error" : ""}
      />
      {showFieldError("clientName") && <small className="error">{fieldErrors.clientName}</small>}

      <input
        placeholder="Client Email"
        value={client.email}
        onChange={(e) => handleClientChange("email", e.target.value)}
        onBlur={() => setTouched((prev) => ({ ...prev, clientEmail: true }))}
        className={showFieldError("clientEmail") ? "input-error" : ""}
      />
      {showFieldError("clientEmail") && <small className="error">{fieldErrors.clientEmail}</small>}

      <input
        placeholder="Company (optional)"
        value={client.company}
        onChange={(e) => handleClientChange("company", e.target.value)}
      />

      <input
        placeholder="Client Phone (optional)"
        value={client.phone}
        onChange={(e) => handleClientChange("phone", e.target.value)}
      />

      <p className="form-section-label">Route</p>
      <input
        ref={fromRef}
        placeholder="From"
        value={locationInputs.from}
        onChange={(e) => handleLocationChange("from", e.target.value)}
        onBlur={() => setTouched((prev) => ({ ...prev, from: true }))}
        className={showFieldError("from") ? "input-error" : ""}
        list={mapsUnavailable ? "origin-suggestions" : undefined}
      />
      {mapsUnavailable && suggestions.from.length > 0 && (
        <datalist id="origin-suggestions">
          {suggestions.from.map((item) => (
            <option key={item} value={item} />
          ))}
        </datalist>
      )}
      {showFieldError("from") && <small className="error">{fieldErrors.from}</small>}

      <input
        ref={toRef}
        placeholder="To"
        value={locationInputs.to}
        onChange={(e) => handleLocationChange("to", e.target.value)}
        onBlur={() => setTouched((prev) => ({ ...prev, to: true }))}
        className={showFieldError("to") ? "input-error" : ""}
        list={mapsUnavailable ? "destination-suggestions" : undefined}
      />
      {mapsUnavailable && suggestions.to.length > 0 && (
        <datalist id="destination-suggestions">
          {suggestions.to.map((item) => (
            <option key={item} value={item} />
          ))}
        </datalist>
      )}
      {showFieldError("to") && <small className="error">{fieldErrors.to}</small>}

      <p className="form-section-label">Load dimensions</p>
      <input
        placeholder="Weight (tonnes)"
        type="number"
        value={vehicle.weight}
        onChange={(e) => handleVehicleChange("weight", e.target.value)}
        onBlur={() => setTouched((prev) => ({ ...prev, weight: true }))}
        className={showFieldError("weight") ? "input-error" : ""}
      />
      {showFieldError("weight") && <small className="error">{fieldErrors.weight}</small>}

      <input
        placeholder="Width"
        type="number"
        value={vehicle.width}
        onChange={(e) => handleVehicleChange("width", e.target.value)}
        onBlur={() => setTouched((prev) => ({ ...prev, width: true }))}
        className={showFieldError("width") ? "input-error" : ""}
      />
      {showFieldError("width") && <small className="error">{fieldErrors.width}</small>}

      <input
        placeholder="Height (m)"
        type="number"
        value={vehicle.height}
        onChange={(e) => handleVehicleChange("height", e.target.value)}
        onBlur={() => setTouched((prev) => ({ ...prev, height: true }))}
        className={showFieldError("height") ? "input-error" : ""}
      />
      {showFieldError("height") && <small className="error">{fieldErrors.height}</small>}

      <input
        placeholder="Length (m) — optional"
        type="number"
        value={vehicle.length}
        onChange={(e) => handleVehicleChange("length", e.target.value)}
      />

      <p className="form-section-label">Equipment</p>
      <select
        value={vehicle.machineType}
        onChange={(e) => handleVehicleChange("machineType", e.target.value)}
        onBlur={() => setTouched((prev) => ({ ...prev, machineType: true }))}
        className={showFieldError("machineType") ? "input-error" : ""}
      >
        <option value="">Machine Type</option>
        <option value="Excavator">Excavator</option>
        <option value="Crane">Crane</option>
        <option value="Bulldozer">Bulldozer</option>
      </select>
      {showFieldError("machineType") && <small className="error">{fieldErrors.machineType}</small>}

      <select
        value={vehicle.machineCategory}
        onChange={(e) => handleVehicleChange("machineCategory", e.target.value)}
      >
        <option value="">Machine Category</option>
        <option value="Construction">Construction</option>
        <option value="Mining">Mining</option>
        <option value="Energy">Energy</option>
        <option value="Industrial">Industrial</option>
        <option value="Agricultural">Agricultural</option>
      </select>

      <select
        value={vehicle.trailerType}
        onChange={(e) => handleVehicleChange("trailerType", e.target.value)}
      >
        <option value="">Trailer Type</option>
        <option value="Lowbed">Lowbed</option>
        <option value="Extendable Lowbed">Extendable Lowbed</option>
        <option value="Multi-Axle Trailer">Multi-Axle Trailer</option>
        <option value="Modular Trailer">Modular Trailer</option>
        <option value="Stepdeck">Stepdeck</option>
      </select>

      <select
        value={vehicle.axleConfiguration}
        onChange={(e) => handleVehicleChange("axleConfiguration", e.target.value)}
      >
        <option value="">Axle Configuration</option>
        <option value="4x2">4x2</option>
        <option value="6x4">6x4</option>
        <option value="8x4">8x4</option>
        <option value="8x6">8x6</option>
        <option value="10x6">10x6</option>
      </select>

      {/* BUTTON */}
      <button type="submit" disabled={loading}>
        {loading ? "Calculating..." : "Generate Quote"}
      </button>

      {/* ERROR */}
      {mapsUnavailable && (
        <p className="muted">
          Autocomplete fallback active (server suggestions/manual typing mode).
        </p>
      )}
      {error && <p className="error">{error}</p>}

    </form>
  );
}